import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { StripeProvider } from "@/components/payment/StripeProvider";
import { AuthModalsProvider } from "@/contexts/AuthModalsContext";
import { SearchModalProvider } from "@/contexts/SearchModalContext";
import { AuthProvider } from "@/auth/AuthProvider";
import { ThemeProvider } from "next-themes";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SkipToContent } from "@/components/ui/skip-to-content";
import { ContentFallback } from "@/components/ui/content-fallback";
import { OfflineOverlay } from "@/components/OfflineOverlay";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { SmartAppBanner } from "@/components/SmartAppBanner";
import { setDeepLinkNavigator } from "@/lib/deepLinkRouter";

import Protected from "./auth/Protected";
import AdminProtected from "./auth/AdminProtected";

// Lazy load route components
const Index = lazy(() => import("./pages/Index"));
const Stays = lazy(() => import("./pages/Stays"));
const HotelDetails = lazy(() => import("./pages/HotelDetails"));
const HotelDetailsLuxury = lazy(() => import("./pages/HotelDetailsLuxury"));


const RequestAccess = lazy(() => import("./pages/RequestAccess"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AccountLayout = lazy(() => import("./pages/account/AccountLayout"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Admin = lazy(() => import("./pages/Admin"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const About = lazy(() => import("./pages/About"));
const RefundsPolicy = lazy(() => import("./pages/RefundsPolicy"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Support = lazy(() => import("./pages/Support"));
const Welcome = lazy(() => import("./pages/Welcome"));
const Invoice = lazy(() => import("./pages/Invoice"));
const Farewell = lazy(() => import("./pages/Farewell"));
const StaySurvey = lazy(() => import("./pages/StaySurvey"));

const queryClient = new QueryClient();

// Preload critical route chunks during idle time
if (typeof window !== 'undefined') {
  const preloadCriticalRoutes = () => {
    import("./pages/Stays");
    import("./pages/account/AccountLayout");
    import("./pages/HotelDetails");
  };
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(preloadCriticalRoutes, { timeout: 3000 });
  } else {
    setTimeout(preloadCriticalRoutes, 2000);
  }
}

const AppContent = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="app-root">
      {children}
    </div>
  );
};

/** Wrapper that forces remount of route content on navigation to kill stale scroll state */
const KeyedRouteContent = ({ children }: { children: React.ReactNode }) => {
  const { pathname, search } = useLocation();
  return <div key={pathname + search}>{children}</div>;
};

/** Bridges deep link navigation into React Router */
const DeepLinkBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setDeepLinkNavigator((path) => navigate(path));
  }, [navigate]);
  return null;
};

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <StripeProvider>
          <AuthProvider>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AuthModalsProvider>
                <SearchModalProvider>
                  <DeepLinkBridge />
                  <OfflineOverlay />
                  <SkipToContent />
                  <ScrollToTop />
                  <Toaster />
                  <AppContent>
                    <ErrorBoundary>
                      <KeyedRouteContent>
                        <Routes>
                          <Route path="/" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <Index />
                            </Suspense>
                          } />
                          
                          <Route path="/stays" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <ErrorBoundary><Stays /></ErrorBoundary>
                            </Suspense>
                          } />

                          <Route path="/search" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <ErrorBoundary><Stays /></ErrorBoundary>
                            </Suspense>
                          } />
                          
                          
                          <Route path="/hotel/:hotelId" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <ErrorBoundary><HotelDetails /></ErrorBoundary>
                            </Suspense>
                          } />
                          
                          <Route path="/hotel-details" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <ErrorBoundary><HotelDetailsLuxury /></ErrorBoundary>
                            </Suspense>
                          } />
                          
                          <Route path="/checkout" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <Protected><ErrorBoundary><Checkout /></ErrorBoundary></Protected>
                            </Suspense>
                          } />
                          
                          <Route path="/booking-confirmation" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <BookingConfirmation />
                            </Suspense>
                          } />
                          
                          <Route path="/request-access" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <RequestAccess />
                            </Suspense>
                          } />
                          
                          <Route path="/forgot-password" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <ForgotPassword />
                            </Suspense>
                          } />
                          
                          <Route path="/reset-password" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <ResetPassword />
                            </Suspense>
                          } />
                          
                          
                          <Route path="/admin" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <AdminProtected><Admin /></AdminProtected>
                            </Suspense>
                          } />
                          
                          
                          <Route path="/account" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <Protected><ErrorBoundary><AccountLayout /></ErrorBoundary></Protected>
                            </Suspense>
                          } />
                          

                          <Route path="/privacy-policy" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <PrivacyPolicy />
                            </Suspense>
                          } />

                          <Route path="/terms" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <TermsAndConditions />
                            </Suspense>
                          } />

                          <Route path="/about" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <About />
                            </Suspense>
                          } />

                          <Route path="/refunds-policy" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <RefundsPolicy />
                            </Suspense>
                          } />

                          <Route path="/help" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <HelpCenter />
                            </Suspense>
                          } />

                          <Route path="/cookie-policy" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <CookiePolicy />
                            </Suspense>
                          } />

                          <Route path="/support" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <Support />
                            </Suspense>
                          } />

                          <Route path="/welcome" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <Welcome />
                            </Suspense>
                          } />

                          <Route path="/invoice" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <Invoice />
                            </Suspense>
                          } />

                          <Route path="/farewell" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <Farewell />
                            </Suspense>
                          } />

                          <Route path="/survey" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <StaySurvey />
                            </Suspense>
                          } />
                          
                          <Route path="*" element={
                            <Suspense fallback={<ContentFallback type="page" />}>
                              <NotFound />
                            </Suspense>
                          } />
                        </Routes>
                      </KeyedRouteContent>
                    </ErrorBoundary>
                  </AppContent>
                  <SmartAppBanner />
                  <CookieConsentBanner />
                </SearchModalProvider>
            </AuthModalsProvider>
              </BrowserRouter>
          </AuthProvider>
        </StripeProvider>
      </TooltipProvider>
    </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
